import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { ProduccionDiariaRow, ProduccionDiariaSortKey } from '@/types'
import { useAllowedEmpresas } from '@/hooks/useAllowedEmpresas'
import { useIsAvpOnly, fmtArticulo } from '@/hooks/useIsAvpOnly'
import { matchesItemType, itemTypesFor, type ItemType } from '@/utils/itemType'
import { henoArticulo } from '@/utils/filtroArticulo'
import { adherenceBand } from '@/utils/adherencia'
import { useVariasEmpresas } from '@/hooks/useVariasEmpresas'

const PAGE_SIZE = 50

// ── Hook (client-side filters only; date range is handled in the page) ────────
/**
 * `paramsIniciales` los aporta la página, que es quien llama a
 * `useFiltrosPersistentes` — también es dueña del rango de fechas, y ese hook se
 * llama UNA vez por pantalla. Son los parámetros de la URL si se llegó con
 * enlace, y si no los que la sesión recuerda; hay que leerlos en los
 * inicializadores, porque después no se vuelve a mirar la URL. Sin él se lee la
 * URL, que es el comportamiento de siempre.
 */
export function useProduccionDiariaFilters(
  rows: ProduccionDiariaRow[],
  paramsIniciales?: URLSearchParams,
) {
  const [searchParams, setSearchParams] = useSearchParams()
  const params0 = paramsIniciales ?? searchParams
  const allowedEmpresas = useAllowedEmpresas()
  const variasEmpresas = useVariasEmpresas()
  const isAvpOnly       = useIsAvpOnly()
  const fmt = useCallback(
    (c: string | null | undefined) => fmtArticulo(c, isAvpOnly),
    [isAvpOnly],
  )

  const [q, setQState] = useState<string[]>(() => (params0.get('q') ?? '').split('|').filter(Boolean))
  const [lote,        setLoteState]        = useState<string[]>(() => (params0.get('lote')  ?? '').split('|').filter(Boolean))
  const [numop,       setNumopState]       = useState<string[]>(() => (params0.get('numop') ?? '').split('|').filter(Boolean))
  const [empresa,     setEmpresaState]     = useState(params0.get('empresa') ?? '')
  const [estado,      setEstadoState]      = useState(params0.get('estado')  ?? '')
  const [familia,     setFamiliaState]     = useState(params0.get('familia') ?? '')
  const [rendimiento, setRendimientoState] = useState(params0.get('rend')    ?? '')
  const [tipo, setTipoState] = useState<Set<ItemType>>(() => {
    const raw = params0.get('tipo')
    const valid = new Set<ItemType>([...itemTypesFor(false), ...itemTypesFor(true)])
    return new Set((raw ? raw.split(',') : []).filter((x): x is ItemType => valid.has(x as ItemType)))
  })
  const [sortKey, setSortKeyState] = useState<ProduccionDiariaSortKey>(
    (params0.get('sort') as ProduccionDiariaSortKey) ?? 'fecha',
  )
  const [sortDir, setSortDirState] = useState<'asc' | 'desc'>(
    (params0.get('dir') as 'asc' | 'desc') ?? 'desc',
  )
  const [page, setPage] = useState(1)

  function setQ(v: string[]) {
    setQState(v); setPage(1)
    const u = v.join('|')
    setSearchParams(p => { u ? p.set('q', u) : p.delete('q'); return p }, { replace: true })
  }
  function setLote(v: string[]) {
    setLoteState(v); setPage(1)
    const u = v.join('|')
    setSearchParams(p => { u ? p.set('lote', u) : p.delete('lote'); return p }, { replace: true })
  }
  function setNumop(v: string[]) {
    setNumopState(v); setPage(1)
    const u = v.join('|')
    setSearchParams(p => { u ? p.set('numop', u) : p.delete('numop'); return p }, { replace: true })
  }
  function setEmpresa(v: string) {
    setEmpresaState(v); setPage(1)
    setSearchParams(p => { v ? p.set('empresa', v) : p.delete('empresa'); return p }, { replace: true })
  }
  function setEstado(v: string) {
    setEstadoState(v); setPage(1)
    setSearchParams(p => { v ? p.set('estado', v) : p.delete('estado'); return p }, { replace: true })
  }
  function setFamilia(v: string) {
    setFamiliaState(v); setPage(1)
    setSearchParams(p => { v ? p.set('familia', v) : p.delete('familia'); return p }, { replace: true })
  }
  function setRendimiento(v: string) {
    setRendimientoState(v); setPage(1)
    setSearchParams(p => { v ? p.set('rend', v) : p.delete('rend'); return p }, { replace: true })
  }
  function setTipo(v: Set<ItemType>) {
    setTipoState(v); setPage(1)
    const csv = [...v].join(',')
    setSearchParams(p => { csv ? p.set('tipo', csv) : p.delete('tipo'); return p }, { replace: true })
  }
  function setSortKey(k: ProduccionDiariaSortKey) {
    setSortKeyState(k)
    setSearchParams(p => { p.set('sort', k); return p }, { replace: true })
  }
  function setSortDir(d: 'asc' | 'desc') {
    setSortDirState(d)
    setSearchParams(p => { p.set('dir', d); return p }, { replace: true })
  }
  function clearFilters() {
    setQState([]); setLoteState([]); setNumopState([]); setEmpresaState('')
    setEstadoState(''); setFamiliaState(''); setRendimientoState(''); setTipoState(new Set())
    setSortKeyState('fecha'); setSortDirState('desc'); setPage(1)
    setSearchParams(p => {
      ['q','lote','numop','empresa','estado','familia','rend','tipo','sort','dir'].forEach(k => p.delete(k))
      return p
    }, { replace: true })
  }

  useEffect(() => { setPage(1) }, [rows.length])

  // ── Rendimiento por OP (cabecera) ────────────────────────────────────────
  // El filtro de rendimiento debe operar sobre el total de la OP
  // (suma de qtyProducida / suma de qtyPlanificada de todas sus líneas),
  // NO sobre el rendimiento de cada línea/lote individual.
  const opRendimientoMap = useMemo(() => {
    // Paso 1: acumular totales por "dataareaid:numOp"
    // IMPORTANTE: qtyPlanificada es la cantidad planificada de la OP completa,
    // repetida igual en cada fila/lote. Se usa Math.max (no suma) para obtener
    // el valor real sin multiplicarlo por el número de lotes.
    // qtyProducida SÍ se suma porque es la producción específica de cada lote.
    const acc = new Map<string, { produced: number; planned: number }>()
    for (const r of rows) {
      const key = `${r.dataareaid}:${r.numOp}`
      const cur = acc.get(key) ?? { produced: 0, planned: 0 }
      acc.set(key, {
        produced: cur.produced + r.qtyProducida,
        planned:  Math.max(cur.planned, r.qtyPlanificada),
      })
    }
    // Paso 2: convertir a porcentaje (null si no hay planificado)
    const result = new Map<string, number | null>()
    for (const [key, { produced, planned }] of acc) {
      result.set(key, planned > 0 ? (produced / planned) * 100 : null)
    }
    return result
  }, [rows])

  // ── Derived options (populated from current dataset) ─────────────────────
  const allEstados = useMemo(() =>
    [...new Set(rows.map(r => r.estadoOp).filter(Boolean) as string[])].sort((a, b) =>
      a.localeCompare(b, 'es-ES')),
  [rows])

  const allFamilias = useMemo(() =>
    [...new Set(rows.map(r => r.familia).filter(Boolean) as string[])].sort((a, b) =>
      a.localeCompare(b, 'es-ES')),
  [rows])

  const avp = isAvpOnly
  const prevAvp = useRef(avp)
  useEffect(() => {
    if (prevAvp.current !== avp) { prevAvp.current = avp; setTipo(new Set()) }
  }, [avp])

  // ── Filtered ──────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    // OR dentro de cada filtro, AND entre filtros distintos.
    const lqs     = q.map(x => x.toLowerCase())
    const llotes  = lote.map(x => x.toLowerCase())
    const lnumops = numop.map(x => x.toLowerCase())
    return rows.filter(r => {
      if (empresa && r.dataareaid !== empresa) return false
      // El código entra CRUDO y FORMATEADO: el catálogo del desplegable manda el
      // crudo, la tabla enseña el formateado, y los dos tienen que encontrar la
      // misma fila.
      if (lqs.length > 0) {
        const texto = henoArticulo(r.articulo, fmt, r.descripcion)
        if (!lqs.some(x => texto.includes(x))) return false
      }
      if (llotes.length > 0) {
        const l = (r.lote ?? '').toLowerCase()
        if (!llotes.some(x => l.includes(x))) return false
      }
      if (lnumops.length > 0) {
        const n = r.numOp.toLowerCase()
        if (!lnumops.some(x => n.includes(x))) return false
      }
      if (estado && r.estadoOp !== estado)   return false
      if (familia && r.familia !== familia)  return false
      if (rendimiento) {
        // Usar el rendimiento de la cabecera (total de la OP), no del lote individual.
        // Los tres tramos son las MISMAS bandas que colorean el %: `high` = en
        // objetivo (98–102 %), `mid` = aviso, `low` = desviación grave. Las claves de
        // la URL no cambian para no romper enlaces guardados.
        const opKey = `${r.dataareaid}:${r.numOp}`
        const pct   = opRendimientoMap.get(opKey) ?? null
        const band  = adherenceBand(pct)
        if (rendimiento === 'high'      && band !== 'green') return false
        if (rendimiento === 'mid'       && band !== 'amber') return false
        if (rendimiento === 'low'       && band !== 'red')   return false
        if (rendimiento === 'unplanned' && pct !== null)     return false
      }
      if (tipo.size > 0 && !matchesItemType(r.articulo, tipo, avp)) return false
      return true
    })
  }, [rows, empresa, q, lote, numop, estado, familia, rendimiento, tipo, opRendimientoMap, avp, fmt])

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      const av = a[sortKey as keyof ProduccionDiariaRow]
      const bv = b[sortKey as keyof ProduccionDiariaRow]
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'number' && typeof bv === 'number') return dir * (av - bv)
      return dir * String(av).localeCompare(String(bv), 'es-ES')
    })
  }, [filtered, sortKey, sortDir])

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const safePage   = Math.min(page, totalPages)
  const paged      = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const activeFilters =
    (q.length > 0 ? 1 : 0) + (lote.length > 0 ? 1 : 0) + (numop.length > 0 ? 1 : 0) + (empresa ? 1 : 0) +
    (estado ? 1 : 0) + (familia ? 1 : 0) + (rendimiento ? 1 : 0) + (tipo.size ? 1 : 0)

  // Mas de UNA empresa que elegir, no el rol `Empresa.All`.
  const showEmpresaFilter = variasEmpresas

  return {
    q, lote, numop, empresa, estado, familia, rendimiento, tipo,
    sortKey, sortDir,
    setQ, setLote, setNumop, setEmpresa, setEstado, setFamilia, setRendimiento, setTipo,
    setSortKey, setSortDir, clearFilters,
    filtered, sorted, paged,
    page: safePage, setPage, totalPages, pageSize: PAGE_SIZE,
    activeFilters, showEmpresaFilter, allowedEmpresas,
    allEstados, allFamilias, avp,
  }
}
